import React from "react";
import { Sparkles } from "lucide-react";
import { IntegrationsStatus } from "@/api/getIntegrationsStatus/types";
import { Button } from "@/components/Button/Button";
import { AccountMappingSection } from "@/modules/hub/features/QuickBooksAccountMapping/components/AccountMappingSection/AccountMappingSection";
import { useAccountMappingSection } from "@/modules/hub/features/QuickBooksAccountMapping/hooks/useAccountMappingSection";
import { AI_GRADIENT_RING_CLASSES } from "@/modules/hub/features/QuickBooksAccountMapping/config/accountMappingDisplay";
import DataSourceCard from "../DataSourceCard/DataSourceCard";
import ConfigurationStep from "../ConfigurationStep/ConfigurationStep";
import QuickBooksConnection from "../QuickBooksConnection/QuickBooksConnection";
import QuickBooksTradeConfiguration from "../QuickBooksTradeConfiguration/QuickBooksTradeConfiguration";
import { MembershipSkuSection } from "@/modules/hub/features/MembershipSkuManagement/components/MembershipSkuSection/MembershipSkuSection";

/**
 * Step 4, in its own component so the mapping hook mounts with the step rather
 * than with the card. `DataSourceCard` renders children only while it is
 * expanded, and calling the hook one level up would fetch the mappings and the
 * catalog for a card nobody has opened.
 *
 * It exists at all because the Auto-map button sits in the step header, beside
 * the title, where it reads as an action on the whole step rather than as one
 * more control in the list it acts on — and the button and the list have to
 * share one call of the hook, so something has to own that call above both.
 */
const AccountMappingStep: React.FC<{ companyUuid: string }> = ({
  companyUuid,
}) => {
  const accountMapping = useAccountMappingSection(companyUuid);
  const { isAutoMapping, isSaving, runAutoMap, unplacedAccounts } =
    accountMapping;

  return (
    <ConfigurationStep
      // Auto-map only ever touches accounts with no mapping yet, so once
      // everything is placed it can do nothing and comes off the screen.
      action={
        unplacedAccounts.length > 0 ? (
          <Button
            className={`rounded-md ${AI_GRADIENT_RING_CLASSES}`}
            disabled={isAutoMapping || isSaving}
            onClick={runAutoMap}
            size="sm"
            variant="ghost"
          >
            <Sparkles />
            {isAutoMapping ? "Mapping accounts…" : "Auto-map accounts"}
          </Button>
        ) : undefined
      }
      description="For each category below, choose the QuickBooks accounts that roll into it. That's what powers your KPIs."
      number={4}
      title="Map your chart of accounts"
    >
      <AccountMappingSection accountMapping={accountMapping} />
    </ConfigurationStep>
  );
};

interface QuickBooksDataSourceProps {
  companyUuid: string;
  intacctId?: string;
  isExpanded: boolean;
  onToggle: () => void;
  status?: IntegrationsStatus["quickBooksOnlineIntegration"];
}

const QuickBooksDataSource: React.FC<QuickBooksDataSourceProps> = ({
  companyUuid,
  intacctId,
  isExpanded,
  onToggle,
  status,
}) => {
  const isConnected = status?.isConnected ?? false;
  const hasSyncableData = status?.hasSyncableData ?? false;
  const hasRunningJobs = status?.hasRunningJobs ?? false;
  const isCategorized =
    (status?.isTradeConfigured ?? false) &&
    (status?.hasActiveMembershipSkus ?? false);

  const syncStatusLabel = hasRunningJobs
    ? "A sync is currently running."
    : hasSyncableData
      ? "QuickBooks data has been imported."
      : "No QuickBooks data has been synced yet.";

  return (
    <DataSourceCard
      capabilities={[
        { label: "Financial KPIs", isActive: isConnected },
        { label: "Customer leads", isActive: isConnected },
      ]}
      id="quickbooks"
      isConnected={isConnected}
      isExpanded={isExpanded}
      logo={
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#2ca01c] text-sm font-bold text-white">
          qb
        </span>
      }
      name="QuickBooks Online"
      onToggle={onToggle}
    >
      <ConfigurationStep
        description="Authorize a secure, read-only connection. This powers both financial reports and customer import."
        isComplete={isConnected}
        number={1}
        title="Connect your account"
      >
        <QuickBooksConnection intacctId={intacctId} />
      </ConfigurationStep>

      <ConfigurationStep
        description="Balance Sheets and Profit & Loss statements sync automatically each month for KPIs and coach review."
        isComplete={hasSyncableData}
        number={2}
        title="Sync financial data"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {syncStatusLabel}
        </p>
      </ConfigurationStep>

      <ConfigurationStep
        description="Choose the trade and membership SKUs used to categorize imported invoices."
        isComplete={isCategorized}
        number={3}
        title="Categorize invoices"
      >
        <div className="space-y-6">
          <QuickBooksTradeConfiguration
            companyUuid={companyUuid}
            isTradeEditable={status?.isTradeEditable ?? false}
          />
          <MembershipSkuSection currentTenant={intacctId} />
        </div>
      </ConfigurationStep>

      <AccountMappingStep companyUuid={companyUuid} />

      <ConfigurationStep
        description="Contact the support team to initiate a QuickBooks Online data import into your account."
        number={5}
        title="Request data import"
      />

      <div className="flex items-center gap-2 border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-slate-700 dark:text-gray-400">
        <svg
          className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        Secure OAuth, read-only. We never store your QuickBooks credentials.
      </div>
    </DataSourceCard>
  );
};

export default QuickBooksDataSource;
